import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import {
  SearchForm,
  Checkbox,
  Item,
  Group,
  FormLabel
} from 'mailup-web-components'

import {
  InputSearchString,
  InputTags,
  InputFilterByField,
  InputType,
  InputFilterFrom,
  InputFilterTo
} from './components'
import { useRecoilValue, useRecoilCallback } from 'recoil'
import {
  resultsStateSearchParams,
  resultsStateEmptyState
} from '../recoil/state/results'
import {
  searchMessagesCallback,
  resetSearchCallback,
  resetSelectionCallback
} from '../recoil/callbacks/callbacks'

import './Search.scss'

const Search = () => {
  const intl = useIntl()
  const [expanded, setExpanded] = useState(false)

  const resultsSearchParams = useRecoilValue(resultsStateSearchParams)
  const isEmptyState = useRecoilValue(resultsStateEmptyState)
  const fetchResults = useRecoilCallback(searchMessagesCallback, [])
  const resetSearch = useRecoilCallback(resetSearchCallback)
  const resetSelection = useRecoilCallback(resetSelectionCallback, [])
  const values = useRef(resultsSearchParams)

  useEffect(() => {
    fetchResults()
    return () => {
      resetSelection()
    }
  }, [])

  useEffect(() => {
    values.current = resultsSearchParams
  }, [resultsSearchParams])

  const handleInputChange = (key, val) => {
    values.current = {
      ...values.current,
      [key]: val
    }
  }

  const handleSubmit = () => {
    resetSelection()
    fetchResults({ ...values.current, pageNumber: 1 })
  }

  const handleReset = () => {
    resetSearch()
  }

  const AdvancedSearchComponent = () => (
    <AdvancedSearch onInputChange={handleInputChange} />
  )

  if (isEmptyState) return null

  return (
    <SearchForm
      advanced
      labelBtnReset={intl.formatMessage({ id: 'Load.MailupCommon.Reset' })}
      labelBtnSearch={intl.formatMessage({ id: 'Load.MailupCommon.Search' })}
      AdvancedSearchComponent={AdvancedSearchComponent}
      expanded={expanded}
      onClickToggle={() => setExpanded(!expanded)}
      onClickReset={handleReset}
      onSubmit={handleSubmit}
      className='c-message-list-search-form'
      submitClassName='test-searchButton'
      resetClassName='test-resetButton'
    >
      <Group>
        <Item>
          <div className='b-message-list-search__text'>
            <InputSearchString
              onChange={e => {
                handleInputChange('searchText', e.target.value)
              }}
            />
          </div>
          <div className='b-message-list-search__in-content'>
            <Checkbox
              label={intl.formatMessage({
                id: 'Load.MailupCommon.SearchFulltext'
              })}
              onChange={e => {
                handleInputChange('searchInContent', e.target.checked)
              }}
              className='test-searchInputFulltext'
              data-mu-test='search__search-in-content'
            />
          </div>
        </Item>
      </Group>
    </SearchForm>
  )
}

const AdvancedSearch = React.memo(({ onInputChange }) => {
  const intl = useIntl()
  return (
    <>
      <Group wide>
        <Item>
          <InputTags onChange={value => onInputChange('tags', value)} />
        </Item>
      </Group>

      <Group of={4}>
        <Item>
          <InputFilterByField
            onChange={value => onInputChange('filterByField', value)}
          />
        </Item>
      </Group>

      <Group of={4}>
        <FormLabel>
          {intl.formatMessage({ id: 'Load.MailupCommon.FromDate' })}
        </FormLabel>
        <Item>
          <InputFilterFrom
            onChange={value => onInputChange('filterByFieldFrom', value)}
          />
        </Item>
      </Group>

      <Group of={4}>
        <FormLabel>
          {intl.formatMessage({ id: 'Load.MailupCommon.ToDate' })}
        </FormLabel>
        <Item>
          <InputFilterTo
            onChange={value => onInputChange('filterByFieldTo', value)}
          />
        </Item>
      </Group>

      <Group of={4}>
        <FormLabel>
          {intl.formatMessage({ id: 'Load.MailupCommon.Type' })}
        </FormLabel>
        <Item>
          <InputType onChange={value => onInputChange('type', value)} />
        </Item>
      </Group>
    </>
  )
})

AdvancedSearch.propTypes = {
  onInputChange: PropTypes.func
}

export default Search
